import type { HelpArticle } from "../../types";
import { MAPLE_PROJECT } from "../../../calculations/calcPreviewFixtures";
import { SCHEMA_SHEET_CAPTURE_FIXTURE } from "../../../calculations/helpCaptureFixtures";

export const sheetLifecycleArticle: HelpArticle = {
  id: "sheet-lifecycle",
  title: "Saving, recalculating & locking sheets",
  description:
    "Follow edits from local state through replacement, distinguish failure from the prior record, and understand the separate load-sheet save/calculate path.",
  tags: ["save", "recalculate", "unsaved changes", "failure", "version", "lock", "load sheet"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A calculation sheet can hold values being edited in the browser, a saved server record, and a saved calculation result. Registry and wood sheets use one computed update. Load sheets save a draft and calculate it in separate requests. Lock is only read-only presentation state.",
    },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, SCHEMA_SHEET_CAPTURE_FIXTURE.mark] },
    {
      kind: "callout",
      variant: "note",
      title: "Lifecycle figure fixture",
      body: `The ${SCHEMA_SHEET_CAPTURE_FIXTURE.mark} captures use a ${SCHEMA_SHEET_CAPTURE_FIXTURE.kind}. They prove visible interface transitions, not steel design results.`,
    },

    { kind: "heading", level: 2, text: "Typed edits begin locally", id: "unsaved" },
    {
      kind: "paragraph",
      text: "On a registry sheet, typing in a number or text field updates local state first. The header immediately says **Unsaved changes**. Blur or Enter commits the value; select and boolean controls commit when changed; a catalog selection commits when applied. **Recalculate** submits the latest local values explicitly.",
    },
    {
      kind: "shot",
      shotId: "sheet-edit-unsaved",
      caption: "A typed fixture value before field exit: the header distinguishes local unsaved state from the saved result.",
    },
    {
      kind: "paragraph",
      text: "Wood section, geometry, condition, and direct-load edits follow the same boundary: typed values wait for blur or Enter, while discrete choices and row actions commit immediately.",
    },

    { kind: "heading", level: 2, text: "Registry and wood commits are computed replacements", id: "success" },
    {
      kind: "paragraph",
      text: "The update endpoint validates and computes before committing the replacement. While the request runs, the previous result remains visible and overlapping actions are disabled. A successful response replaces the saved calculation and result together. Unsaved changes clears only when no newer local edit remains.",
    },
    {
      kind: "shot",
      shotId: "sheet-recalculation-success",
      caption: "The deterministic interface fixture after field exit: a successful response replaces the visible result record.",
    },
    {
      kind: "paragraph",
      text: "Rapid commits are serialized. If a later edit arrives while one request is active, the newer snapshot waits and runs next, preventing an older response from becoming the final state. Registry and wood update requests currently do not send `expectedVersion`.",
    },

    { kind: "heading", level: 2, text: "A failed replacement preserves the prior record", id: "failure" },
    {
      kind: "paragraph",
      text: "Validation, catalog resolution, or calculation can reject the attempted replacement. The schema workspace adds **Latest recalculation failed** above the prior successful result and prints the API error. The error describes the attempt; the check cards beneath it still describe the previous record.",
    },
    {
      kind: "shot",
      shotId: "sheet-recalculation-failed",
      caption: "A deterministic rejected request leaves the earlier interface-fixture record visible beneath Latest recalculation failed.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Do not read the old record as the attempted edit",
      body: "Correct the inputs and commit again. Until a replacement succeeds, the error and prior result must be read together.",
    },

    { kind: "heading", level: 2, text: "Load sheets save, then calculate", id: "load-sheets" },
    {
      kind: "paragraph",
      text: "A new load-calculation sheet starts as **Draft** with no result. A committed input change first saves the draft, then calls the dedicated calculate endpoint using the saved version. A name-only change can save without rerunning because it does not change the input revision. Edits made during a run queue one trailing calculation.",
    },
    {
      kind: "paragraph",
      text: "Both load draft-save and calculate requests send `expectedVersion`. If another session saves first, the API returns a 409 conflict instead of overwriting the newer row. Reload the sheet, reconcile its current inputs, and commit again. This optimistic-concurrency path is specific to load sheets today.",
    },
    {
      kind: "paragraph",
      text: "While load inputs are locally dirty, the workspace hides the prior result rather than pairing it with visibly changed inputs. A successful draft save clears the prior result and verdict fields and returns the sheet to Draft before calculation. If calculation then fails, the newer draft remains saved and the old result remains cleared. A rejected draft save does not advance to calculation.",
    },
    {
      kind: "callout",
      variant: "info",
      title: "Load statuses are engine states, not member verdicts",
      body: "A load result can be Complete, Partial, Blocked, or Not applicable. For example, wind components-and-cladding with roof, wall, and overhang scopes all disabled returns Not applicable. Load results use the nominal_load calculation kind and do not carry a member pass/fail verdict.",
    },
    {
      kind: "paragraph",
      text: "Most nominal-load rows identify their factor basis as `service_unfactored`. Snow rows identify `asce7_22_reliability_targeted` and carry the tested recommended factors LRFD 1.0 and ASD 0.7. Load sheets are not recomputed when the calculation project's ASD/LRFD default changes.",
    },

    { kind: "heading", level: 2, text: "Upstream changes can make downstream sheets out of date", id: "downstream" },
    {
      kind: "paragraph",
      text: "A successful source recomputation can change its reaction packet. The project marks affected downstream sheets OUT OF DATE. Recompute all settles the graph upstream before downstream so inherited packets come from the newest successful source records.",
    },

    { kind: "heading", level: 2, text: "Lock changes presentation only", id: "lock" },
    {
      kind: "paragraph",
      text: "Lock disables input and catalog interaction and places a presentation banner over the existing result. It does not save, recalculate, approve, stamp, sign, or change the stored inputs or result. Unlock restores editing without contacting the engine.",
    },
    {
      kind: "shot",
      shotId: "sheet-locked-record",
      caption: "The locked deterministic fixture: read-only presentation over the unchanged saved result.",
    },
    {
      kind: "paragraph",
      text: "Quick and Detailed are also presentation choices. Use the saved status, returned method and references, and the absence of failure or out-of-date banners to understand provenance; do not infer approval from a locked or compact view.",
    },

    { kind: "related", topics: ["schema-sheets", "member-sheets", "design-method", "linking-reactions"] },
  ],
};
